import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Building2,
  Coins,
  Gift,
  Home,
  MessageCircle,
  Monitor,
  Percent,
  Printer,
  Settings,
  Shield,
  Store,
  Users,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleProvider";
import { can, isOnlineFeatureEnabled } from "@/lib/permissions";
import { cn } from "@/lib/utils";
import "@/styles/simple-ui.css";

const MODULES = [
  { to: "/admin/general", labelKey: "nav.generalSettings", hintKey: "simple.settingsGeneralHint", icon: Settings, bg: "bg-[#475569]", show: (p) => can(p, "administration.view") },
  { to: "/admin/company", labelKey: "nav.companySetup", hintKey: "simple.settingsCompanyHint", icon: Building2, bg: "bg-[#2563EB]", show: (p) => can(p, "administration.view") },
  { to: "/admin/restaurant", labelKey: "nav.restaurantSetup", hintKey: "simple.settingsRestaurantHint", icon: Store, bg: "bg-[#F97316]", show: (p) => can(p, "administration.view") },
  { to: "/admin/counters", labelKey: "nav.counters", hintKey: "simple.settingsCountersHint", icon: Monitor, bg: "bg-[#0D9488]", show: (p) => can(p, "administration.view") },
  { to: "/admin/tax", labelKey: "nav.taxSettings", hintKey: "simple.settingsTaxHint", icon: Percent, bg: "bg-[#D97706]", show: (p) => can(p, "administration.view") },
  { to: "/admin/printers", labelKey: "nav.printers", hintKey: "simple.settingsPrintersHint", icon: Printer, bg: "bg-[#64748B]", show: (p) => can(p, "administration.view") },
  { to: "/admin/users", labelKey: "nav.users", hintKey: "simple.settingsUsersHint", icon: Users, bg: "bg-[#7C3AED]", show: (p) => can(p, "administration.view") },
  { to: "/admin/roles", labelKey: "nav.roles", hintKey: "simple.settingsRolesHint", icon: Shield, bg: "bg-[#9333EA]", show: (p) => can(p, "administration.view") },
  { to: "/admin/loyalty", labelKey: "nav.loyalty", hintKey: "simple.settingsLoyaltyHint", icon: Gift, bg: "bg-[#DB2777]", show: (p) => can(p, "administration.view") },
  { to: "/admin/tips", labelKey: "nav.tips", hintKey: "simple.settingsTipsHint", icon: Coins, bg: "bg-[#CA8A04]", show: (p) => can(p, "administration.view") },
  { to: "/admin/whatsapp", labelKey: "nav.whatsapp", hintKey: "simple.settingsWhatsappHint", icon: MessageCircle, bg: "bg-[#16A34A]", show: (p) => can(p, "administration.view") },
  {
    to: "/admin/online-store",
    labelKey: "nav.onlineStore",
    hintKey: "simple.settingsOnlineHint",
    icon: Store,
    bg: "bg-[#DC2626]",
    show: (p, user) => can(p, "online.orders.view") && isOnlineFeatureEnabled(user),
  },
];

export default function SimpleSettingsHub() {
  const { t } = useTranslation();
  const { isRtl } = useLocale();
  const { user } = useAuth();
  const permissions = user?.permissions || [];

  const tiles = useMemo(
    () => MODULES.filter((m) => m.show(permissions, user)),
    [permissions, user],
  );

  return (
    <div className="simple-ui flex h-full flex-col" dir={isRtl ? "rtl" : "ltr"} data-testid="simple-settings-hub">
      <div className="flex items-center gap-2 border-b bg-white px-3 py-2">
        <Link to="/" className="inline-flex h-10 items-center gap-1 rounded-xl bg-violet-50 px-3 text-sm font-bold text-violet-700">
          <Home className="h-4 w-4" />
          {t("common.home")}
        </Link>
        <h1 className="text-lg font-extrabold text-slate-900">{t("nav.administration")}</h1>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <p className="mb-4 text-center text-sm text-slate-500 sm:text-start">
          {t("simple.settingsSubtitle", "Company, counters, tax, printers, and staff")}
        </p>
        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {tiles.map((mod) => {
            const Icon = mod.icon;
            return (
              <Link
                key={mod.to}
                to={mod.to}
                className={cn("simple-tile flex min-h-[120px] flex-col items-center justify-center gap-2 p-4 text-center text-white", mod.bg)}
              >
                <Icon className="h-8 w-8" strokeWidth={2.25} />
                <span className="text-[14px] font-extrabold leading-tight">{t(mod.labelKey)}</span>
                <span className="text-[11px] font-medium text-white/85">{t(mod.hintKey)}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
